/* eslint-disable no-nested-ternary */
import { useState } from 'react';
import PropTypes from 'prop-types';
import {
    TableRow,
    TableCell,
    Typography,
    IconButton,
    Box,
    Tooltip,
    Chip,
} from '@mui/material';
// use components
import { Iconify } from '../../../components';
import { deleteScriptSubmission } from '../../../redux/slices/callScript';
import { followUpSlice } from '../../../redux/slices/lead';
import { dispatch, useSelector } from '../../../redux/store';
import { ConfirmDialog } from '../../../components/custom-dialog';

// ---------------------------------------------------------
export default function ScriptTableRow({ row, selected, onOpenDetailSection }) {
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);

    const handleDelete = () => {
        dispatch(deleteScriptSubmission(practiceId, row?.form?.id, row?.id, handleSuccess));
        setDeleteDialogOpen(false);
    };

    const handleSuccess = (data) => {
        if (data?.success) {
            dispatch(followUpSlice(row.lead, practiceId, 'LeadDetail'));
        }
    };

    const handleRowClick = () => {
        onOpenDetailSection(row?.form?.id, row.id);
    };

    // Get category badge color
    const getCategoryColor = (category) => {
        const colors = {
            INBOUND: { bg: '#E8F5E9', color: '#2E7D32' },
            OUTBOUND: { bg: '#E3F2FD', color: '#1565C0' },
        };
        return colors[category] || { bg: '#F5F5F5', color: '#616161' };
    };

    const categoryStyle = getCategoryColor(row?.form?.category);

    return (
        <>
            <TableRow
                hover
                selected={selected}
                onClick={handleRowClick}
                sx={{
                    cursor: 'pointer',
                    borderBottom: 'solid 1px rgba(145, 158, 171, 0.16)',
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                        backgroundColor: 'rgba(0, 167, 111, 0.04)',
                    },
                    '&:hover .action-buttons': {
                        opacity: 1,
                    },
                }}
            >
                {/* Date Column */}
                <TableCell sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(0, 167, 111, 0.08)',
                            }}
                        >
                            <Iconify
                                icon="mdi:clipboard-text-outline"
                                width={20}
                                height={20}
                                sx={{ color: '#00A76F' }}
                            />
                        </Box>
                        <Box>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 500,
                                    color: '#212B36',
                                    fontSize: '0.875rem',
                                }}
                            >
                                {row.created_at_human}
                            </Typography>
                        </Box>
                    </Box>
                </TableCell>

                {/* Name Column */}
                <TableCell align="left" sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 600,
                                color: '#212B36',
                                textTransform: 'capitalize',
                                fontSize: '0.875rem',
                            }}
                        >
                            {row?.form?.name || 'Untitled Script'}
                        </Typography>
                        {row?.form?.category && (
                            <Chip
                                label={row.form.category.toLowerCase()}
                                size="small"
                                sx={{
                                    height: 22,
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    textTransform: 'capitalize',
                                    backgroundColor: categoryStyle.bg,
                                    color: categoryStyle.color,
                                    '& .MuiChip-label': {
                                        px: 1,
                                    },
                                }}
                            />
                        )}
                    </Box>
                </TableCell>

                {/* Actions Column */}
                <TableCell align="right" sx={{ py: 2, pr: 2 }}>
                    <Box
                        className="action-buttons"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 0.5,
                            opacity: 0,
                            transition: 'opacity 0.2s ease',
                        }}
                    >
                        <Tooltip title="Open">
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRowClick();
                                }}
                                sx={{
                                    color: '#637381',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 167, 111, 0.08)',
                                        color: '#00A76F',
                                    },
                                }}
                            >
                                <Iconify icon="mdi:open-in-new" width={18} height={18} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteDialogOpen(true);
                                }}
                                sx={{
                                    color: '#637381',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 86, 48, 0.08)',
                                        color: '#FF5630',
                                    },
                                }}
                            >
                                <Iconify icon="mdi:delete-outline" width={18} height={18} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </TableCell>
            </TableRow>

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                title="Delete Submission"
                content="Are you sure you want to delete this call script submission? This action cannot be undone."
                action={
                    <IconButton
                        onClick={handleDelete}
                        sx={{
                            backgroundColor: '#FF5630',
                            color: '#fff',
                            borderRadius: '8px',
                            px: 2,
                            '&:hover': {
                                backgroundColor: '#B71D18',
                            },
                        }}
                    >
                        <Typography variant="button" sx={{ color: '#fff', mr: 0.5 }}>
                            Delete
                        </Typography>
                        <Iconify icon="mdi:delete-outline" width={18} height={18} />
                    </IconButton>
                }
            />
        </>
    );
}

ScriptTableRow.propTypes = {
    row: PropTypes.object,
    selected: PropTypes.bool,
    onOpenDetailSection: PropTypes.func.isRequired,
};
